import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { enableTOTP, verifyTOTPSetup } from '../services/mfaService';
import { getErrorMessage } from '../utils/getErrorMessage';
import '../styles/MFASettings.css';

interface MFASettingsProps {
  onSetupComplete?: () => void;
}

type SetupStep = 'initial' | 'qr_code' | 'verify' | 'backup_codes';

export function MFASettings({ onSetupComplete }: MFASettingsProps) {
  const [step, setStep] = useState<SetupStep>('initial');
  const [secret, setSecret] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEnableMFA = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await enableTOTP();
      setSecret(data.secret);
      setProvisioningUri(data.provisioning_uri);
      setStep('qr_code');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to enable MFA'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verifyCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await verifyTOTPSetup(verifyCode);
      setBackupCodes(data.backup_codes);
      setStep('backup_codes');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Invalid code'));
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const text = `TACOLCY CRM - Backup Codes\n\nSave these codes in a secure location.\nEach code can only be used once.\n\n${backupCodes.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tacolcy-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  const completeSetup = () => {
    setStep('initial');
    setVerifyCode('');
    setSecret('');
    setProvisioningUri('');
    setBackupCodes([]);
    onSetupComplete?.();
  };

  if (step === 'initial') {
    return (
      <div className="mfa-settings">
        <h3>Two-Factor Authentication</h3>
        <p>Add an extra layer of security to your account.</p>

        {error && <div className="alert alert-error">{error}</div>}

        <button
          className="btn-primary"
          onClick={handleEnableMFA}
          disabled={loading}
        >
          {loading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
        </button>
      </div>
    );
  }

  if (step === 'qr_code') {
    return (
      <div className="mfa-settings">
        <h3>Scan QR Code</h3>
        <p>Use your authenticator app to scan this QR code.</p>

        <div className="qr-code-container">
          <QRCodeSVG
            value={provisioningUri}
            size={256}
            level="H"
            includeMargin={true}
          />
        </div>

        <div className="manual-entry">
          <p>Or enter this code manually:</p>
          <code className="secret-code">{secret}</code>
        </div>

        <p className="apps-note">
          Compatible apps: Google Authenticator, Microsoft Authenticator, Authy, 1Password
        </p>

        <button className="btn-primary" onClick={() => setStep('verify')}>
          I&apos;ve Scanned the Code
        </button>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="mfa-settings">
        <h3>Verify Setup</h3>
        <p>Enter the 6-digit code from your authenticator app.</p>

        {error && <div className="alert alert-error">{error}</div>}

        <input
          type="text"
          className="verify-code-input"
          value={verifyCode}
          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          maxLength={6}
          autoFocus
        />

        <div className="button-group">
          <button
            className="btn-secondary"
            onClick={() => setStep('qr_code')}
            disabled={loading}
          >
            Back
          </button>
          <button
            className="btn-primary"
            onClick={handleVerifyCode}
            disabled={loading || verifyCode.length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'backup_codes') {
    return (
      <div className="mfa-settings">
        <h3>Save Your Backup Codes</h3>
        <div className="alert alert-warning">
          <strong>Important:</strong> Save these codes in a secure location.
          Each code can only be used once. These codes will NOT be shown again.
        </div>

        <div className="backup-codes-grid">
          {backupCodes.map((code, index) => (
            <div key={index} className="backup-code">
              {code}
            </div>
          ))}
        </div>

        <div className="button-group">
          <button className="btn-secondary" onClick={downloadBackupCodes}>
            Download Codes
          </button>
          <button className="btn-primary" onClick={completeSetup}>
            I&apos;ve Saved My Codes
          </button>
        </div>
      </div>
    );
  }

  return null;
}
